import {
  ActionSheetButton,
  IonActionSheet,
  IonAvatar,
  IonIcon,
} from '@ionic/react';
import { camera } from 'ionicons/icons';
import { ReactElement, useState } from 'react';
import {
  AttachmentSourceType,
  User,
  useDeleteAttachmentMutation,
  useUpdateUserMutation,
} from '../../api/graphql/generated';
import {
  ImageResolution,
  OnFileUploadResult,
  useUploadAttachmentHelper,
} from '../../api/hooks/attachment.hooks';
import { getLocalizedText } from '../../i18n/i18n.utils';
import { logger } from '../../util/logger';
import { useWithConfirmation } from '../button/with-confirmation';
import { SaveStatusToast } from '../toast/toast.gql';
import { Avatar } from './avatar';
import './avatar-edit.css';

interface AvatarEditProps {
  user: Partial<User>;
  resolution?: ImageResolution;
}

const AvatarEdit = (props: AvatarEditProps): ReactElement => {
  const { user, resolution } = props;

  const [updateUser, { loading: isUpdatingUser }] = useUpdateUserMutation();
  const [deleteAttachment] = useDeleteAttachmentMutation();
  const [removeAvatarLoading, setRemoveAvatarLoading] = useState(false);
  const [removeAvatarError, setRemoveAvatarError] = useState(false);
  const { openFile, isUploading, uploader } = useUploadAttachmentHelper({
    sources: [
      { source: AttachmentSourceType.USER_AVATAR, sourceId: user.id as string },
    ],
    accept: 'image/x-png,image/jpeg',
    onFileUpload: async (result: OnFileUploadResult) => {
      if (result.fileKey && result.attachment?.id) {
        await updateUser({
          variables: {
            input: {
              id: user.id,
              email: user.email,
              authMethod: user.authMethod,
              avatarAttachment: {
                id: result.attachment.id,
              },
            },
          },
        });
      }
    },
  });

  const withConfirmation = useWithConfirmation();

  const removeAvatar = async () => {
    setRemoveAvatarError(false);
    const avatarId = user?.avatarAttachment?.id;

    if (avatarId) {
      try {
        setRemoveAvatarLoading(true);

        // We need to remove the association first
        const updateUserResult = await updateUser({
          variables: {
            input: {
              id: user.id,
              email: user.email,
              authMethod: user.authMethod,
              avatarAttachment: null,
            },
          },
        });

        if (updateUserResult.data === undefined) {
          logger.error('failed to disassociate avatar attachment from user', {
            user,
          });
          setRemoveAvatarError(true);
          setRemoveAvatarLoading(false);
          return;
        }

        const deleteResult = await deleteAttachment({
          variables: {
            input: {
              id: avatarId,
            },
          },
        });

        if (!deleteResult.data?.deleteAttachment) {
          // Reset the avatar association
          await updateUser({
            variables: {
              input: {
                id: user.id,
                email: user.email,
                authMethod: user.authMethod,
                avatarAttachment: {
                  id: avatarId,
                },
              },
            },
          });

          logger.error('failed to delete avatar', {
            user,
          });
          setRemoveAvatarError(true);
        }
      } catch (error) {
        logger.error('failed to delete avatar', {
          error,
          user,
        });
        setRemoveAvatarError(true);
      } finally {
        setRemoveAvatarLoading(false);
      }
    }
  };

  const actionButtons: ActionSheetButton[] = [
    {
      text: getLocalizedText('common:upload'),
      handler: openFile,
    },
    {
      text: getLocalizedText('common:cancel'),
      role: 'cancel',
    },
  ];

  if (user.avatarAttachment?.id) {
    actionButtons.push({
      text: getLocalizedText('common:remove'),
      role: 'destructive',
      handler: () => withConfirmation({ onConfirmation: removeAvatar }),
    });
  }

  return (
    <>
      <div id="open-avatar-options" className="avatar-edit">
        <div style={{ position: 'relative' }}>
          <div className="avatar-edit-icon">
            <IonAvatar>
              <IonIcon icon={camera} />
            </IonAvatar>
          </div>
          <div>
            <Avatar
              attachment={user.avatarAttachment}
              updating={isUploading || isUpdatingUser || removeAvatarLoading}
              resolution={resolution}
            />
            {uploader}
          </div>
        </div>
      </div>
      <IonActionSheet trigger="open-avatar-options" buttons={actionButtons} />
      <SaveStatusToast
        showOnly="error"
        error={removeAvatarError}
        loading={removeAvatarLoading}
        defaultErrorMessageKey={'common:avatar_removal_failed'}
      />
    </>
  );
};

export { AvatarEdit };
